import { routerReducer } from 'react-router-redux';
import reduceReducers from 'reduce-reducers';
import { createStore, compose, applyMiddleware, combineReducers } from 'redux';
import thunk from 'redux-thunk';
import { appReducer } from './AppStore';
import ActionThatChangesStore from './ActionThatChangesStore';
import { manageReducer } from './ManageStore';
import { themeReducer } from './ThemeStore';
import { saveReducer } from './SaveStore';
import { frontmanReducer } from './FrontmanStore';
import imageUploadReducer from '../reducers/images/assetUpload';
import { regexForWordList } from 'components/helpers/WordSearchRegexCreator';
import { configurationActions, thirdpartyActions } from '../constants/configurationListItems';
import * as ActionTypes from '../constants/ActionTypes';

import { reducer as OAuthReducer, isAuthReducer } from '../../lib/redux-oauth2';
import fontsDataReducer from '../reducers/theme/data/fonts';

const logger = ({ getState }) => next => action => {
    console.log('---> REDUX LOGGER: will dispatch', action);
    // Call the next dispatch method in the middleware chain.
    const returnValue = next(action);
    console.log('---> REDUX LOGGER: state after dispatch', getState());
    // This will likely be the action itself, unless
    // a middleware further in chain changed it.
    return returnValue;
};

/**

  Put every action.type which should trigger STORE_CHANGED
  in "./ActionThatChangesStore.js"!

 */
const storeChanged = ({ dispatch, getState }) => next => action => {
    if (ActionThatChangesStore.indexOf(action.type) >= 0) {
        const { type } = action;
        const state = getState();
        if (type.match(regexForWordList(configurationActions))) {
            dispatch({
                type: 'EDIT_STORE_CHANGED',
                configChange: true,
                thirdpartyChange: state.save.thirdpartyChange,
                themeChange: state.save.themeChange,
                themeChangeCategory: state.save.themeChangeCategory || 'SBE',
                payload: action
            });
        } else if (type.match(regexForWordList(thirdpartyActions))) {
            dispatch({
                type: 'EDIT_STORE_CHANGED',
                configChange: state.save.configChange,
                themeChange: state.save.themeChange,
                themeChangeCategory: state.save.themeChangeCategory || 'SBE',
                thirdpartyChange: true,
                payload: action
            });
        } else {
            dispatch({
                type: 'EDIT_STORE_CHANGED',
                themeChange: true,
                themeChangeCategory: 'SBE',  // Regular theme changes are SBE category
                configChange: state.save.configChange,
                thirdpartyChange: state.save.thirdpartyChange,
                payload: action
            });
        }
    }
    return next(action);
};

/**
 * Middleware to handle LOGIN_SET_THEME action for SBEM themes
 * Automatically dispatches LOGIN_SET_IN_CONTEXT_THEME when an SBEM theme is selected
 */
const themeSetMiddleware = store => next => action => {
    const result = next(action);
    
    // After the action has been processed, check if it's LOGIN_SET_THEME
    if (action.type === ActionTypes.LOGIN_SET_THEME) {
        if (action.isSbemTheme && action.theme) {
            store.dispatch({
                type: ActionTypes.LOGIN_SET_IN_CONTEXT_THEME,
                theme: action.theme
            });
        }
    }
    
    return result;
};

const reducer = reduceReducers(
    combineReducers({
        save: saveReducer,
        manage: manageReducer,
        theme: themeReducer,
        app: appReducer,
        routing: routerReducer,
        assetUpload: imageUploadReducer,
        authReducer: isAuthReducer,
        frontman: frontmanReducer,
        ...OAuthReducer
    }),
    fontsDataReducer
);

/* eslint-disable no-underscore-dangle */
if (process.env.NODE_ENV === 'DEV') {
    console.log('---> ENV: DEVELOPMENT');
}

const actionListenersStoreEnhancer = createStore => {
    return (reducer, initialState, enhancer) => {
        const actionListeners = {};
        const store = createStore(reducer, initialState, enhancer);
        const dispatch = store.dispatch;
        store.dispatch = action => {
            const result = dispatch(action);
            if (typeof action === 'object' && action.type && actionListeners[action.type]) {
                actionListeners[action.type].forEach(listener => listener(action));
            }
            return result;
        };
        store.addActionListener = (actionType, listener) => {
            actionListeners[actionType] = (actionListeners[actionType] || []).concat(listener);
            return () => {
                actionListeners[actionType] = actionListeners[actionType].filter(l => l !== listener);
            };
        };
        return store;
    };
};
/**
 * Helper function for create story during unit tests
 * @returns {*}
 */
export function storeFactory() {
    return process.env.NODE_ENV === 'DEV'
        ? compose(actionListenersStoreEnhancer)(createStore)(
              reducer,
              window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__(),
              // applyMiddleware(thunk, logger),
              applyMiddleware(storeChanged, themeSetMiddleware, thunk)
          )
        : compose(actionListenersStoreEnhancer)(createStore)(reducer, applyMiddleware(storeChanged, themeSetMiddleware, thunk));
}
const store = storeFactory();

export default store;



// WEBPACK FOOTER //
// ./src/store/Store.js